import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { firstAllowedPath, type AppModule } from '@vce/shared';
import { AppLayout } from './layout';
import { useAuth } from './auth-context';
import { TenantProvider } from './tenant-context';
import { AdminPage } from '@/pages/admin-page';
import { DashboardPage } from '@/pages/dashboard-page';
import { LandingPage } from '@/pages/landing-page';
import { LoginPage } from '@/pages/login-page';
import { NoAccessPage } from '@/pages/no-access-page';
import { ProductsPage } from '@/pages/products-page';
import { ReviewPage } from '@/pages/review-page';
import { RunsPage } from '@/pages/runs-page';
import { SettingsPage } from '@/pages/settings-page';

function RequireAuth() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  );
}

function RequireOwner() {
  const { user } = useAuth();
  if (user?.role !== 'owner') {
    return <Navigate to={user ? firstAllowedPath(user) : '/login'} replace />;
  }
  return <Outlet />;
}

function RequireModuleRoute({ module }: { module: AppModule }) {
  const { user, hasModule } = useAuth();
  if (!hasModule(module)) {
    return <Navigate to={user ? firstAllowedPath(user) : '/login'} replace />;
  }
  return <Outlet />;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route path="/no-access" element={<NoAccessPage />} />
          <Route element={<RequireOwner />}>
            <Route path="/admin" element={<AdminPage />} />
          </Route>
          <Route element={<RequireModuleRoute module="dashboard" />}>
            <Route path="/dashboard" element={<DashboardPage />} />
          </Route>
          <Route element={<RequireModuleRoute module="products" />}>
            <Route path="/products" element={<ProductsPage />} />
          </Route>
          <Route element={<RequireModuleRoute module="review" />}>
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/history" element={<Navigate to="/review?tab=history" replace />} />
          </Route>
          <Route element={<RequireModuleRoute module="runs" />}>
            <Route path="/runs" element={<RunsPage />} />
          </Route>
          <Route element={<RequireModuleRoute module="write_operations" />}>
            <Route
              path="/write-operations"
              element={<Navigate to="/runs?tab=publications" replace />}
            />
          </Route>
          <Route element={<RequireModuleRoute module="settings" />}>
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
